<template>
  <Modal
    content-class="q-px-lg q-pt-lg"
    full-height
    header-class="q-px-lg q-py-md"
    :modal-style="{ maxWidth: '1000px' }"
    title="Filter Contacts in Outreach"
  >
    <div class="q-mb-sm">
      <div class="bold q-mb-sm">Filter by location</div>
      <div class="row no-wrap">
        <QToggle
          v-model="allOutreachLocations"
          color="primary"
          label="All Locations"
          @update:model-value="updateAllOutreachLocations"
        />
      </div>
    </div>
    <div
      v-if="locations"
      class="q-pb-lg col-6"
    >
      <OutreachLocationSelector
        :locations="locations"
        :selected-locations="currentLocationFilters"
        @toggle-country="toggleCountry"
        @toggle-region="toggleRegion"
      />
    </div>
    <template #buttons>
      <BaseButton
        aria-label="Cancel"
        color="secondary"
        :disabled="loading"
        label="Cancel"
        new-design
        @click="close"
      />
      <div>
        <BaseButton
          color="primary"
          :disabled="isInvalid"
          label="Apply"
          :loading="loading"
          new-design
          @click="save"
        />
        <StreemTooltip
          v-if="isInvalid"
          anchor="top middle"
          no-caret
          self="center middle"
        >
          Please select at least one location
        </StreemTooltip>
      </div>
    </template>
  </Modal>
</template>

<script>
import { isEqual } from "lodash-es";

import { BaseButton } from "shared/components/base";
import Modal from "shared/components/core/modals/Modal.vue";
import OutreachLocationSelector from "shared/components/selectors/OutreachLocationSelector.vue";
import {
  Organisation,
  OrganisationOutreachLocationExclusion,
  OutreachLocation,
} from "shared/resources";

const VALID_LOCATION_TYPES = [
  "Continent",
  "Sub-Continent",
  "Country",
  "Area of Interest",
];

export default {
  name: "OutreachLocationRestrictionModal",
  components: {
    Modal,
    BaseButton,
    OutreachLocationSelector,
  },
  props: {
    organisationId: {
      type: [Number, String],
      required: true,
    },
  },
  emits: ["afterSave"],
  data() {
    return {
      allOutreachLocations: null,
      organisation: null,
      locations: [],
      loading: false,
    };
  },
  computed: {
    currentLocationFilters() {
      return (
        this.organisation?.organisationOutreachLocationExclusions
          .filter(
            ({ outreachLocationId, isMarkedForDestruction }) =>
              outreachLocationId && !isMarkedForDestruction
          )
          .map((location) => location.outreachLocationId) || []
      );
    },
    isInvalid() {
      const filteredLocations = this.locations
        .filter((location) =>
          VALID_LOCATION_TYPES.includes(location.locationType)
        )
        .map(({ id }) => id);

      return this.currentLocationFilters?.length === filteredLocations.length;
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    loadOrganistionWithOutreachFilters() {
      return Organisation.includes(
        "organisationOutreachLocationExclusions"
      ).find(this.organisationId);
    },
    loadOutreachContinentsAndCountries() {
      return OutreachLocation.where({
        locationType: VALID_LOCATION_TYPES,
      })
        .selectExtra(["selfAndParentNames"])
        .order({ name: "asc" })
        .per(999)
        .all();
    },
    async loadData() {
      const promises = [
        this.loadOrganistionWithOutreachFilters(),
        this.loadOutreachContinentsAndCountries(),
      ];

      try {
        this.loading = true;
        const responses = await Promise.all(promises);

        this.organisation = responses[0].data;

        this.locations = responses[1].data;

        this.allOutreachLocations = this.setAllOutreachLocations();
      } catch (error) {
        this.$error("Error loading OUTREACH location filters", error);
      } finally {
        this.loading = false;
      }
    },
    toggleRegion(location, { value, toggleChildren }) {
      const foundRecord =
        this.organisation.organisationOutreachLocationExclusions.find(
          (exclusion) => exclusion.outreachLocationId === location.id
        );

      const childLocations = this.locationsForParent(location.id);

      if (foundRecord) {
        this.markForDestruction(foundRecord, value);
      } else {
        this.organisation.organisationOutreachLocationExclusions.push(
          new OrganisationOutreachLocationExclusion({
            organisationId: this.organisation.id,
            outreachLocationId: location.id,
          })
        );
      }

      if (toggleChildren) {
        childLocations.forEach((childLocation) => {
          this.toggleCountry(childLocation, foundRecord, value);
        });
      }
    },
    toggleCountry(location, parentLocation, value) {
      const foundRecord =
        this.organisation.organisationOutreachLocationExclusions.find(
          (exclusion) => exclusion.outreachLocationId === location.id
        );

      const foundParentRecord =
        this.organisation.organisationOutreachLocationExclusions.find(
          (exclusion) =>
            exclusion.outreachLocationId === parentLocation?.outreachLocationId
        );

      if (!foundRecord && foundParentRecord && value) {
        return;
      }

      if (foundRecord) {
        this.markForDestruction(foundRecord, value);
      } else {
        this.organisation.organisationOutreachLocationExclusions.push(
          new OrganisationOutreachLocationExclusion({
            organisationId: this.organisation.id,
            outreachLocationId: location.id,
          })
        );
      }

      this.checkLastCountryUpdated(location, value);
    },
    checkLastCountryUpdated(childLocation, value) {
      const parentLocation = this.locations.find(
        (location) => location.id === childLocation.parentId
      );

      // Check if this is the last child checked
      const childLocations = this.locationsForParent(parentLocation.id).map(
        ({ id }) => id
      );

      const uncheckedRecords = childLocations.filter((location) =>
        this.currentLocationFilters.includes(location)
      );

      if (
        !uncheckedRecords.length ||
        childLocations.length <= uncheckedRecords.length
      ) {
        this.toggleRegion(parentLocation, { value, toggleChildren: false });
      }
    },
    markForDestruction(object, value) {
      Object.assign(object, { isMarkedForDestruction: value });
    },
    setAllOutreachLocations() {
      if (this.locations.length && !this.currentLocationFilters.length) {
        return true;
      }

      const allLocationIds = this.locations.map(({ id }) => id);

      return isEqual(this.currentLocationFilters, allLocationIds);
    },
    updateAllOutreachLocations() {
      const topLevelLocations = this.locations.filter(
        (location) =>
          VALID_LOCATION_TYPES.includes(location.locationType) &&
          location.name !== "CIS"
      );

      if (this.allOutreachLocations) {
        topLevelLocations.forEach((location) => {
          if (this.currentLocationFilters.includes(location.id)) {
            this.toggleRegion(location, { value: true, toggleChildren: true });
          }
        });
      } else {
        topLevelLocations.forEach((location) => {
          this.toggleRegion(location, { value: false, toggleChildren: true });
        });
      }
    },
    async save() {
      try {
        this.loading = true;

        await this.organisation.save({
          with: "organisationOutreachLocationExclusions",
        });

        this.$emit("afterSave");
        this.close();
      } catch (error) {
        this.$error("Error saving OUTREACH location filters", error);
      } finally {
        this.loading = false;
      }
    },
    close() {
      this.$modals.close("OutreachLocationRestrictionModal");
    },
    locationsForParent(id) {
      return this.locations.filter(
        (location) =>
          location.parentId === id &&
          location.name !== "Central America" &&
          location.name !== "South America"
      );
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.checkbox) {
  margin-top: 20px;
  margin-bottom: 0;
}
</style>
